import React, { useState, useEffect, useCallback } from 'react';
import {
  Settings, Briefcase, Layers, ArrowRightLeft, Truck, BarChart3,
  Plus, Trash2, Loader2, Save, ChevronRight, Edit2, X, AlertTriangle, CheckCircle2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCompany } from '../context/CompanyContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'settings', label: 'Settings', icon: Settings },
  { id: 'enterprises', label: 'Enterprises', icon: Briefcase },
  { id: 'operations', label: 'Operations', icon: Layers },
  { id: 'allocations', label: 'Allocations', icon: ArrowRightLeft },
  { id: 'equipment', label: 'Equipment', icon: Truck },
  { id: 'reports', label: 'Reports', icon: BarChart3 },
];

const ENT_TYPES = [
  { value: 'overhead', label: 'Overhead / Indirect' },
  { value: 'shop', label: 'Shop (Repairs & Maintenance)' },
  { value: 'cost_center', label: 'Cost Center' },
  { value: 'profit_center', label: 'Profit Center' },
];

const OP_TYPES = [
  { value: 'income', label: 'Income (1-15)' },
  { value: 'income_sum', label: 'Income Sum (16)' },
  { value: 'wage_benefits', label: 'Wage-Benefits (17)' },
  { value: 'expense', label: 'Expense (18-98)' },
  { value: 'allocation', label: 'Allocation (99)' },
];

// ==================== SETTINGS TAB ====================
const SettingsTab = ({ settings, onSave }) => {
  const [form, setForm] = useState({
    accounting_level: 1,
    enterprise_digits: 3,
    operation_digits: 2,
    labels: {},
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setForm({
        accounting_level: settings.accounting_level || 1,
        enterprise_digits: settings.enterprise_digits || 3,
        operation_digits: settings.operation_digits || 2,
        labels: settings.labels || {},
      });
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const levelDescriptions = {
    1: 'All transaction grouping occurs within the Chart of Accounts. No enterprise tracking.',
    2: 'Simplified cost tracking. Transactions tagged with enterprise number only.',
    3: 'Most common. Transactions tagged with enterprise + operation codes (tasks/job phases).',
    4: 'Adds equipment tracking to enterprise + operation. Track costs by piece of equipment.',
    5: 'Full cost accounting. Equipment charge-out rates enable quasi-profit center analysis.',
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Enterprise Accounting Level</CardTitle>
          <p className="text-sm text-slate-500">Select the level of detail for cost accounting</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {Object.entries(settings?.level_options || {}).map(([key, label]) => (
            <label
              key={key}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                form.accounting_level === Number(key)
                  ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-500'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`accounting-level-${key}`}
            >
              <input
                type="radio"
                name="level"
                value={key}
                checked={form.accounting_level === Number(key)}
                onChange={() => setForm({ ...form, accounting_level: Number(key) })}
                className="mt-1"
              />
              <div>
                <span className="font-medium text-slate-800">{label}</span>
                <p className="text-xs text-slate-500 mt-0.5">{levelDescriptions[Number(key)]}</p>
              </div>
            </label>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Code Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Enterprise Number Digits</label>
              <Input
                type="number" min={1} max={6}
                value={form.enterprise_digits}
                onChange={e => setForm({ ...form, enterprise_digits: Number(e.target.value) })}
              />
              <p className="text-xs text-slate-400 mt-1">e.g., 3 digits = 001-999</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Operation Code Digits</label>
              <Input
                type="number" min={1} max={4}
                value={form.operation_digits}
                onChange={e => setForm({ ...form, operation_digits: Number(e.target.value) })}
              />
              <p className="text-xs text-slate-400 mt-1">e.g., 2 digits = 01-99</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Custom Labels</CardTitle>
          <p className="text-sm text-slate-500">Customize terminology for your industry (e.g., agriculture, manufacturing)</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            {['enterprise', 'operation', 'equipment', 'cost_center', 'profit_center', 'category', 'units'].map(key => (
              <div key={key}>
                <label className="text-xs font-medium text-slate-500 uppercase">{key.replace('_', ' ')}</label>
                <Input
                  value={form.labels[key] || ''}
                  onChange={e => setForm({ ...form, labels: { ...form.labels, [key]: e.target.value } })}
                  placeholder={key.replace('_', ' ')}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
      </div>
    </div>
  );
};

// ==================== ENTERPRISES TAB ====================
// Enterprise code ranges follow the configured length: with 3 digits 001 Overhead, 002 Shop, 003-099 categories, 100-999 centers.
const enterpriseRanges = (digits) => {
  const d = Math.min(Math.max(Number(digits) || 3, 2), 8);
  const pad = (n) => String(n).padStart(d, '0');
  const split = 10 ** (d - 1);
  return { overhead: pad(1), shop: pad(2), categories: `${pad(3)}-${pad(split - 1)}`, centers: `${pad(split)}-${pad(10 ** d - 1)}` };
};

const EnterprisesTab = ({ companyId, enterprises, onRefresh, allEnterprises, settings }) => {
  const ranges = enterpriseRanges(settings?.enterprise_digits);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    number: '', name: '', category: '', enterprise_type: 'cost_center',
    parent_number: '', description: '', budget: 0, units: 0, unit_label: 'units',
  });

  const handleCreate = async () => {
    if (!form.number || !form.name) { toast.error('Number and Name are required'); return; }
    try {
      const res = await fetch(`${API_URL}/api/cost-accounting/enterprises`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, budget: Number(form.budget), units: Number(form.units), company_id: companyId }),
      });
      if (res.ok) {
        toast.success('Enterprise created');
        setShowAdd(false);
        setForm({ number: '', name: '', category: '', enterprise_type: 'cost_center', parent_number: '', description: '', budget: 0, units: 0, unit_label: 'units' });
        onRefresh();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed');
      }
    } catch { toast.error('Error creating enterprise'); }
  };

  const handleDelete = async (number) => {
    if (!window.confirm(`Delete enterprise ${number}?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/cost-accounting/enterprises/${number}?company_id=${companyId}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Deleted'); onRefresh(); } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch { toast.error('Failed'); }
  };

  const typeColors = {
    overhead: 'bg-amber-100 text-amber-700 border-amber-200',
    shop: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    cost_center: 'bg-sky-100 text-sky-700 border-sky-200',
    profit_center: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">
            <span className="font-medium text-amber-600" data-testid="range-overhead">{ranges.overhead}</span> = Overhead &nbsp;
            <span className="font-medium text-indigo-600">{ranges.shop}</span> = Shop &nbsp;
            <span className="font-medium text-slate-600" data-testid="range-categories">{ranges.categories}</span> = Categories &nbsp;
            <span className="font-medium text-sky-600" data-testid="range-centers">{ranges.centers}</span> = Cost/Profit Centers
          </p>
        </div>
        <Button onClick={() => setShowAdd(true)} data-testid="add-enterprise-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Enterprise
        </Button>
      </div>

      {enterprises.length === 0 ? (
        <Card className="text-center py-12">
          <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No enterprises configured</p>
          <p className="text-xs text-slate-400 mt-1">Start with Overhead ({ranges.overhead}), Shop ({ranges.shop}), then add categories and cost centers</p>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="enterprises-table">
            <thead>
              <tr className="border-b bg-slate-50 text-left">
                <th className="p-3 font-medium text-slate-600">Number</th>
                <th className="p-3 font-medium text-slate-600">Name</th>
                <th className="p-3 font-medium text-slate-600">Type</th>
                <th className="p-3 font-medium text-slate-600">Category</th>
                <th className="p-3 font-medium text-slate-600">Parent</th>
                <th className="p-3 font-medium text-slate-600 text-right">Budget</th>
                <th className="p-3 font-medium text-slate-600 text-right">Units</th>
                <th className="p-3 font-medium text-slate-600 text-right">Direct Costs</th>
                <th className="p-3 font-medium text-slate-600 text-right">Cost/Unit</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {enterprises.map(ent => (
                <tr key={ent.number} className="border-b hover:bg-slate-50 transition-colors">
                  <td className="p-3 font-mono font-bold text-slate-800">{ent.number}</td>
                  <td className="p-3 font-medium">{ent.name}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded text-xs border ${typeColors[ent.enterprise_type] || 'bg-gray-100'}`}>
                      {ent.enterprise_type?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="p-3 text-slate-500">{ent.category || '-'}</td>
                  <td className="p-3 font-mono text-slate-500">{ent.parent_number || '-'}</td>
                  <td className="p-3 text-right">${(ent.budget || 0).toLocaleString()}</td>
                  <td className="p-3 text-right">{ent.units || 0} {ent.unit_label || ''}</td>
                  <td className="p-3 text-right font-medium text-red-600">${(ent.total_costs || 0).toLocaleString()}</td>
                  <td className="p-3 text-right text-slate-600">${(ent.cost_per_unit || 0).toLocaleString()}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => handleDelete(ent.number)} className="text-slate-400 hover:text-red-500" data-testid={`delete-ent-${ent.number}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Enterprise Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Add Enterprise</CardTitle>
              <button onClick={() => setShowAdd(false)}><X className="w-5 h-5" /></button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Number *</label>
                  <Input value={form.number} onChange={e => setForm({ ...form, number: e.target.value })} placeholder="e.g., 001" data-testid="ent-number-input" />
                </div>
                <div>
                  <label className="text-sm font-medium">Name *</label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Overhead" data-testid="ent-name-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Type</label>
                  <select className="w-full border rounded-md p-2 text-sm" value={form.enterprise_type} onChange={e => setForm({ ...form, enterprise_type: e.target.value })}>
                    {ENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Category</label>
                  <Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="e.g., Crop, Facility" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Parent Enterprise #</label>
                  <select className="w-full border rounded-md p-2 text-sm" value={form.parent_number} onChange={e => setForm({ ...form, parent_number: e.target.value })}>
                    <option value="">None</option>
                    {allEnterprises.map(e => <option key={e.number} value={e.number}>{e.number} - {e.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Budget</label>
                  <Input type="number" value={form.budget} onChange={e => setForm({ ...form, budget: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium">Units</label>
                  <Input type="number" value={form.units} onChange={e => setForm({ ...form, units: e.target.value })} placeholder="0" />
                </div>
                <div>
                  <label className="text-sm font-medium">Unit Label</label>
                  <Input value={form.unit_label} onChange={e => setForm({ ...form, unit_label: e.target.value })} placeholder="acres, units, sqft" />
                </div>
                <div>
                  <label className="text-sm font-medium">Description</label>
                  <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                </div>
              </div>
            </CardContent>
            <div className="flex justify-end gap-2 p-4 border-t">
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={handleCreate} data-testid="create-enterprise-btn">Create</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// ==================== OPERATIONS TAB ====================
const OperationsTab = ({ companyId, operations, onRefresh }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', op_type: 'expense' });

  const handleCreate = async () => {
    if (!form.code || !form.name) { toast.error('Code and Name required'); return; }
    try {
      const res = await fetch(`${API_URL}/api/cost-accounting/operations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, company_id: companyId }),
      });
      if (res.ok) { toast.success('Operation created'); setShowAdd(false); setForm({ code: '', name: '', op_type: 'expense' }); onRefresh(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Error'); }
  };

  const handleDelete = async (code) => {
    try {
      const res = await fetch(`${API_URL}/api/cost-accounting/operations/${code}?company_id=${companyId}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Deleted'); onRefresh(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Error'); }
  };

  const initOps = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cost-accounting/operations/initialize?company_id=${companyId}`, { method: 'POST' });
      if (res.ok) { toast.success('Default operations initialized'); onRefresh(); } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch { toast.error('Failed'); }
  };

  const typeColors = {
    income: 'bg-emerald-100 text-emerald-700',
    income_sum: 'bg-emerald-50 text-emerald-600',
    wage_benefits: 'bg-purple-100 text-purple-700',
    expense: 'bg-red-100 text-red-700',
    allocation: 'bg-amber-100 text-amber-700',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          <span className="text-emerald-600 font-medium">01-15</span> Income &nbsp;
          <span className="text-emerald-500 font-medium">16</span> Income Sum &nbsp;
          <span className="text-purple-600 font-medium">17</span> Wage-Benefits &nbsp;
          <span className="text-red-600 font-medium">18-98</span> Expenses &nbsp;
          <span className="text-amber-600 font-medium">99</span> Allocations
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={initOps}>Initialize Defaults</Button>
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-operation-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Operation
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="operations-table">
          <thead>
            <tr className="border-b bg-slate-50 text-left">
              <th className="p-3 font-medium text-slate-600 w-20">Code</th>
              <th className="p-3 font-medium text-slate-600">Name</th>
              <th className="p-3 font-medium text-slate-600">Type</th>
              <th className="p-3 font-medium text-slate-600">Reserved</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {operations.map(op => (
              <tr key={op.code} className="border-b hover:bg-slate-50">
                <td className="p-3 font-mono font-bold">{op.code}</td>
                <td className="p-3">{op.name}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded text-xs ${typeColors[op.op_type || op.type] || 'bg-gray-100'}`}>
                    {op.op_type || op.type}
                  </span>
                </td>
                <td className="p-3">
                  {op.reserved ? <span className="text-xs text-slate-400">System</span> : <span className="text-xs text-slate-400">Custom</span>}
                </td>
                <td className="p-3 text-right">
                  {!op.reserved && (
                    <button onClick={() => handleDelete(op.code)} className="text-slate-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Add Operation Code</CardTitle>
              <button onClick={() => setShowAdd(false)}><X className="w-5 h-5" /></button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Code *</label>
                  <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="e.g., 20" data-testid="op-code-input" />
                </div>
                <div>
                  <label className="text-sm font-medium">Type</label>
                  <select className="w-full border rounded-md p-2 text-sm" value={form.op_type} onChange={e => setForm({ ...form, op_type: e.target.value })}>
                    {OP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Name *</label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Irrigation" data-testid="op-name-input" />
              </div>
            </CardContent>
            <div className="flex justify-end gap-2 p-4 border-t">
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={handleCreate} data-testid="create-operation-btn">Create</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// ==================== ALLOCATIONS TAB ====================
const AllocationsTab = ({ companyId, allocations, enterprises, onRefresh }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ source_enterprise: '', target_enterprise: '', percentage: 0, show_detail: false });

  const handleCreate = async () => {
    if (!form.source_enterprise || !form.target_enterprise || !form.percentage) {
      toast.error('Source, Target and Percentage are required'); return;
    }
    try {
      const res = await fetch(`${API_URL}/api/cost-accounting/allocations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, percentage: Number(form.percentage), company_id: companyId }),
      });
      if (res.ok) { toast.success('Allocation created'); setShowAdd(false); setForm({ source_enterprise: '', target_enterprise: '', percentage: 0, show_detail: false }); onRefresh(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Error'); }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/cost-accounting/allocations/${id}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Deleted'); onRefresh(); } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Allocate costs from overhead or shared enterprises to cost/profit centers. Max 100% from any source.</p>
        <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-allocation-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Allocation
        </Button>
      </div>

      {allocations.length === 0 ? (
        <Card className="text-center py-12">
          <ArrowRightLeft className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No allocations configured</p>
          <p className="text-xs text-slate-400 mt-1">Allocate overhead costs to cost centers based on percentage</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {allocations.map(a => (
            <Card key={a.allocation_id} className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-center">
                    <span className="font-mono font-bold text-lg text-slate-800">{a.source_enterprise}</span>
                    <p className="text-xs text-slate-500">{a.source_name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-12 h-0.5 bg-slate-300" />
                    <span className="px-2 py-1 bg-sky-100 text-sky-700 rounded font-bold text-sm">{a.percentage}%</span>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                    <div className="w-12 h-0.5 bg-slate-300" />
                  </div>
                  <div className="text-center">
                    <span className="font-mono font-bold text-lg text-slate-800">{a.target_enterprise}</span>
                    <p className="text-xs text-slate-500">{a.target_name}</p>
                  </div>
                  
                </div>
                <button onClick={() => handleDelete(a.allocation_id)} className="text-slate-400 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Add Allocation</CardTitle>
              <button onClick={() => setShowAdd(false)}><X className="w-5 h-5" /></button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Source Enterprise (allocate FROM)</label>
                <select className="w-full border rounded-md p-2 text-sm" value={form.source_enterprise} onChange={e => setForm({ ...form, source_enterprise: e.target.value })} data-testid="alloc-source-select">
                  <option value="">Select...</option>
                  {enterprises.map(e => <option key={e.number} value={e.number}>{e.number} - {e.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Target Enterprise (allocate TO)</label>
                <select className="w-full border rounded-md p-2 text-sm" value={form.target_enterprise} onChange={e => setForm({ ...form, target_enterprise: e.target.value })} data-testid="alloc-target-select">
                  <option value="">Select...</option>
                  {enterprises.filter(e => e.number !== form.source_enterprise).map(e => <option key={e.number} value={e.number}>{e.number} - {e.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Percentage</label>
                <Input type="number" min={0} max={100} step={0.1} value={form.percentage} onChange={e => setForm({ ...form, percentage: e.target.value })} data-testid="alloc-pct-input" />
              </div>
              <p className="text-xs text-slate-500">Whether the target report lists the allocated amounts in detail is chosen each time the report is run.</p>
            </CardContent>
            <div className="flex justify-end gap-2 p-4 border-t">
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={handleCreate} data-testid="create-allocation-btn">Create</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// ==================== EQUIPMENT TAB ====================
const EquipmentTab = ({ companyId, equipReport }) => {
  const data = equipReport?.equipment || [];
  const totals = equipReport?.totals || {};

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">Equipment charge-out analysis. Set charge-out rates to view equipment as quasi-profit centers.</p>

      {data.length === 0 ? (
        <Card className="text-center py-12">
          <Truck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No equipment found</p>
          <p className="text-xs text-slate-400 mt-1">Add equipment via View &rarr; Equipment, then set charge-out rates here</p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4">
            <Card className="p-4 text-center">
              <p className="text-xs text-slate-500">Total Actual Costs</p>
              <p className="text-xl font-bold text-red-600">${(totals.total_actual_costs || 0).toLocaleString()}</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-xs text-slate-500">Total Chargeout Revenue</p>
              <p className="text-xl font-bold text-emerald-600">${(totals.total_chargeout_revenue || 0).toLocaleString()}</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-xs text-slate-500">Net Position</p>
              <p className={`text-xl font-bold ${(totals.net_position || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                ${(totals.net_position || 0).toLocaleString()}
              </p>
            </Card>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="equipment-report-table">
              <thead>
                <tr className="border-b bg-slate-50 text-left">
                  <th className="p-3 font-medium text-slate-600">Equipment</th>
                  <th className="p-3 font-medium text-slate-600">Category</th>
                  <th className="p-3 font-medium text-slate-600 text-right">Chargeout Rate</th>
                  <th className="p-3 font-medium text-slate-600 text-right">Actual Costs</th>
                  <th className="p-3 font-medium text-slate-600 text-right">Chargeout Revenue</th>
                  <th className="p-3 font-medium text-slate-600 text-right">Net</th>
                  <th className="p-3 font-medium text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.map(eq => (
                  <tr key={eq.equipment_id} className="border-b hover:bg-slate-50">
                    <td className="p-3 font-medium">{eq.name}</td>
                    <td className="p-3 text-slate-500">{eq.category}</td>
                    <td className="p-3 text-right">${(eq.charge_out_rate || 0).toFixed(2)}/hr</td>
                    <td className="p-3 text-right text-red-600">${(eq.actual_costs || 0).toLocaleString()}</td>
                    <td className="p-3 text-right text-emerald-600">${(eq.chargeout_revenue || 0).toLocaleString()}</td>
                    <td className={`p-3 text-right font-medium ${eq.net_position >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      ${(eq.net_position || 0).toLocaleString()}
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${eq.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                        {eq.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// ==================== REPORTS TAB ====================
const ReportsTab = ({ companyId }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showDetail, setShowDetail] = useState(false);

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = `company_id=${companyId}${categoryFilter ? `&category=${categoryFilter}` : ''}${showDetail ? '&show_detail=true' : ''}`;
      const res = await fetch(`${API_URL}/api/cost-accounting/enterprise-report?${params}`);
      if (res.ok) setReport(await res.json());
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div>
          <label className="text-xs font-medium text-slate-500">Filter by Category</label>
          <Input value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} placeholder="All categories" className="w-48" />
        </div>
        <label className="flex items-center gap-2 text-sm mt-5">
          <input type="checkbox" checked={showDetail} onChange={e => setShowDetail(e.target.checked)} data-testid="report-show-detail" />
          Show allocation detail
        </label>
        <Button onClick={loadReport} disabled={loading} className="mt-5" data-testid="load-report-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
          Generate Report
        </Button>
      </div>

      {report && (
        <>
          <div className="grid grid-cols-4 gap-4">
            <Card className="p-4 text-center">
              <p className="text-xs text-slate-500">Direct Costs</p>
              <p className="text-lg font-bold text-red-600">${(report.totals?.total_direct || 0).toLocaleString()}</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-xs text-slate-500">Allocated Costs</p>
              <p className="text-lg font-bold text-amber-600">${(report.totals?.total_allocated || 0).toLocaleString()}</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-xs text-slate-500">Total Costs</p>
              <p className="text-lg font-bold text-slate-800">${(report.totals?.total_costs || 0).toLocaleString()}</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-xs text-slate-500">Total Budget</p>
              <p className="text-lg font-bold text-sky-600">${(report.totals?.total_budget || 0).toLocaleString()}</p>
            </Card>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cost-report-table">
              <thead>
                <tr className="border-b bg-slate-50 text-left">
                  <th className="p-2 font-medium text-slate-600">#</th>
                  <th className="p-2 font-medium text-slate-600">Enterprise</th>
                  <th className="p-2 font-medium text-slate-600">Type</th>
                  <th className="p-2 font-medium text-slate-600 text-right">Units</th>
                  <th className="p-2 font-medium text-slate-600 text-right">Budget</th>
                  <th className="p-2 font-medium text-slate-600 text-right">Direct</th>
                  <th className="p-2 font-medium text-slate-600 text-right">Allocated</th>
                  <th className="p-2 font-medium text-slate-600 text-right">Total</th>
                  <th className="p-2 font-medium text-slate-600 text-right">$/Unit</th>
                  <th className="p-2 font-medium text-slate-600 text-right">Variance</th>
                </tr>
              </thead>
              <tbody>
                {report.report?.flatMap(row => [
                  <tr key={row.number} className="border-b hover:bg-slate-50">
                    <td className="p-2 font-mono font-bold">{row.number}</td>
                    <td className="p-2 font-medium">{row.name}</td>
                    <td className="p-2 text-xs text-slate-500">{row.enterprise_type?.replace('_', ' ')}</td>
                    <td className="p-2 text-right">{row.units} {row.unit_label}</td>
                    <td className="p-2 text-right">${(row.budget || 0).toLocaleString()}</td>
                    <td className="p-2 text-right text-red-600">${(row.direct_costs || 0).toLocaleString()}</td>
                    <td className="p-2 text-right text-amber-600">${(row.allocated_costs || 0).toLocaleString()}</td>
                    <td className="p-2 text-right font-medium">${(row.total_costs || 0).toLocaleString()}</td>
                    <td className="p-2 text-right">${(row.cost_per_unit || 0).toLocaleString()}</td>
                    <td className={`p-2 text-right font-medium ${row.variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      ${(row.variance || 0).toLocaleString()}
                    </td>
                  </tr>,
                  ...(row.allocation_detail || []).map((d, i) => (
                    <tr key={`${row.number}-alloc-${i}`} className="border-b bg-slate-50/60 text-xs text-slate-600" data-testid="allocation-detail-row">
                      <td className="p-2" />
                      <td className="p-2 pl-6" colSpan={6}>Allocated from enterprise {d.source_enterprise}: {d.percentage}% of ${(d.source_total || 0).toLocaleString()}</td>
                      <td className="p-2 text-right">${(d.amount || 0).toLocaleString()}</td>
                      <td className="p-2" colSpan={2} />
                    </tr>
                  )),
                ])}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// ==================== MAIN PAGE ====================
const CostAccountingPage = () => {
  const { currentCompany } = useCompany();
  const companyId = currentCompany?.company_id || 'default';

  const [activeTab, setActiveTab] = useState('settings');
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [enterprises, setEnterprises] = useState([]);
  const [operations, setOperations] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [equipReport, setEquipReport] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, eRes, oRes, aRes, eqRes] = await Promise.all([
        fetch(`${API_URL}/api/cost-accounting/settings?company_id=${companyId}`),
        fetch(`${API_URL}/api/cost-accounting/enterprises?company_id=${companyId}`),
        fetch(`${API_URL}/api/cost-accounting/operations?company_id=${companyId}`),
        fetch(`${API_URL}/api/cost-accounting/allocations?company_id=${companyId}`),
        fetch(`${API_URL}/api/cost-accounting/equipment-report?company_id=${companyId}`),
      ]);
      if (sRes.ok) setSettings(await sRes.json());
      if (eRes.ok) setEnterprises(await eRes.json());
      if (oRes.ok) setOperations(await oRes.json());
      if (aRes.ok) setAllocations(await aRes.json());
      if (eqRes.ok) setEquipReport(await eqRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveSettings = async (form) => {
    try {
      const res = await fetch(`${API_URL}/api/cost-accounting/settings?company_id=${companyId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) { toast.success('Settings saved'); const data = await res.json(); setSettings(data); }
      else toast.error('Failed to save settings');
    } catch { toast.error('Error'); }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }

  const levelName = settings?.level_options?.[settings?.accounting_level] || 'Not configured';

  return (
    <div className="space-y-6" data-testid="cost-accounting-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Cost Accounting</h1>
          <p className="text-slate-500">Enterprise cost tracking, operations, allocations, and equipment management</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-400">Current Level</p>
          <p className="text-sm font-medium text-sky-700">{levelName}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b" data-testid="cost-accounting-tabs">
        {TABS.map(tab => {
          const Icon = tab.icon;
          // Hide tabs based on accounting level
          if (settings?.accounting_level < 2 && ['enterprises', 'operations', 'allocations', 'equipment', 'reports'].includes(tab.id)) return null;
          if (settings?.accounting_level < 3 && ['operations'].includes(tab.id)) return null;
          if (settings?.accounting_level < 4 && ['equipment'].includes(tab.id)) return null;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-sky-500 text-sky-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'settings' && <SettingsTab settings={settings} onSave={saveSettings} />}
      {activeTab === 'enterprises' && <EnterprisesTab companyId={companyId} enterprises={enterprises} onRefresh={fetchAll} allEnterprises={enterprises} settings={settings} />}
      {activeTab === 'operations' && <OperationsTab companyId={companyId} operations={operations} onRefresh={fetchAll} />}
      {activeTab === 'allocations' && <AllocationsTab companyId={companyId} allocations={allocations} enterprises={enterprises} onRefresh={fetchAll} />}
      {activeTab === 'equipment' && <EquipmentTab companyId={companyId} equipReport={equipReport} />}
      {activeTab === 'reports' && <ReportsTab companyId={companyId} />}
    </div>
  );
};

export default CostAccountingPage;
